import { getLocalStorageUserSha } from '@/utils/storage';
import isSSR from '@calm/web-common/dist/isSSR';
import {
    GOALS_TOPICS_INFO_VIDEO_ID,
    CONDITIONS_INFO_VIDEO_ID,
    SCREENING_INFO_VIDEO_ID,
    INTRO_TO_CARE_PLAN_VIDEO_ID,
} from '@rhg/utils/constants';
import { FtueStepDefinitionIds } from '@rhg/utils/workflows';
import { LastKnownWorkflowStep } from '../types';

const FTUE_STORED_WORKFLOW_STEP_KEY = 'FTUE_WORKFLOW_STEP';

/**
 * Helper function to store current step in session storage
 */
export const storeCurrentStep = (name: string, stepId: string) => {
    sessionStorage.setItem(name, stepId);
};

/**
 * Helper function to retrieve the stored current step from session storage
 */
export const getStoredStep = (name: string): string | null =>
    sessionStorage.getItem(name);

/**
 * Helper function to remove the stored current step from session storage
 */
export const removeStoredStep = (name: string): void => {
    sessionStorage.removeItem(name);
};

/**
 * Helper function to store workflow step data in session storage
 */
export const storeWorkflowData = (name: string, data: unknown) => {
    sessionStorage.setItem(name, JSON.stringify(data));
};

/**
 * Helper function to retrieve workflow step data from session storage
 */
export const getStoredWorkflowData = <T>(name: string): T | null => {
    const stored = sessionStorage.getItem(name);
    if (!stored) return null;
    try {
        return JSON.parse(stored) as T;
    } catch {
        return null;
    }
};

/**
 * Helper function to remove stored workflow step data from session storage
 */
export const removeStoredWorkflowData = (name: string): void => {
    sessionStorage.removeItem(name);
};

/**
 * Helper function to mark the retaking-survey flag in session storage
 */
export const storeIsRetakingSurvey = (name: string): void => {
    sessionStorage.setItem(name, 'true');
};

/**
 * Helper function to read the retaking-survey flag from session storage
 */
export const getIsRetakingSurvey = (name: string): boolean =>
    sessionStorage.getItem(name) === 'true';

/**
 * Helper function to store the combined workflow step in session storage
 */
export const storeWorkflowStep = (data: LastKnownWorkflowStep) => {
    sessionStorage.setItem(FTUE_STORED_WORKFLOW_STEP_KEY, JSON.stringify(data));
};

/**
 * Helper function to retrieve the combined workflow step from session storage
 */
export const getStoredWorkflowStep = (): LastKnownWorkflowStep | null => {
    const stored = sessionStorage.getItem(FTUE_STORED_WORKFLOW_STEP_KEY);
    if (!stored) return null;
    try {
        return JSON.parse(stored) as LastKnownWorkflowStep;
    } catch {
        return null;
    }
};

/**
 * Helper function to remove the combined workflow step from session storage
 */
export const removeStoredWorkflowStep = (): void => {
    sessionStorage.removeItem(FTUE_STORED_WORKFLOW_STEP_KEY);
};

/**
 * Helper function to check if video is cached in local storage
 */
export const getCachedVideoId = async (value: string, userSub?: string) => {
    if (isSSR()) return;
    switch (value as FtueStepDefinitionIds) {
        case FtueStepDefinitionIds.ONBOARDING_VIDEO_1:
            try {
                const storageValue = await getLocalStorageUserSha(
                    GOALS_TOPICS_INFO_VIDEO_ID,
                    userSub
                );
                // eslint-disable-next-line no-restricted-syntax
                return localStorage.getItem(storageValue);
            } catch {
                // eslint-disable-next-line no-restricted-syntax
                return localStorage.getItem(GOALS_TOPICS_INFO_VIDEO_ID);
            }
        case FtueStepDefinitionIds.ONBOARDING_VIDEO_2:
            try {
                const storageValue = await getLocalStorageUserSha(
                    CONDITIONS_INFO_VIDEO_ID,
                    userSub
                );
                // eslint-disable-next-line no-restricted-syntax
                return localStorage.getItem(storageValue);
            } catch {
                // eslint-disable-next-line no-restricted-syntax
                return localStorage.getItem(CONDITIONS_INFO_VIDEO_ID);
            }
        case FtueStepDefinitionIds.ONBOARDING_VIDEO_3:
            try {
                const storageValue = await getLocalStorageUserSha(
                    SCREENING_INFO_VIDEO_ID,
                    userSub
                );
                // eslint-disable-next-line no-restricted-syntax
                return localStorage.getItem(storageValue);
            } catch {
                // eslint-disable-next-line no-restricted-syntax
                return localStorage.getItem(SCREENING_INFO_VIDEO_ID);
            }
        case FtueStepDefinitionIds.ONBOARDING_VIDEO_4:
        case FtueStepDefinitionIds.NEW_ONBOARDING_VIDEO_4:
            try {
                const storageValue = await getLocalStorageUserSha(
                    INTRO_TO_CARE_PLAN_VIDEO_ID,
                    userSub
                );
                // eslint-disable-next-line no-restricted-syntax
                return localStorage.getItem(storageValue);
            } catch {
                // eslint-disable-next-line no-restricted-syntax
                return localStorage.getItem(INTRO_TO_CARE_PLAN_VIDEO_ID);
            }
        default:
            return;
    }
};
